/**
 * El nombre con el que un equipo se LEE, armado desde la ficha.
 *
 * POR QUE HACE FALTA
 * ------------------
 * El nombre guardado dejo de contar toda la historia a proposito: el color y
 * la bateria salieron de adentro del nombre y pasaron a ser columnas (0055,
 * 0056), porque un dato que cambia —la bateria se vuelve a medir, y baja— no
 * puede ser parte del identificador. Un nombre que se mueve no sirve para
 * buscar, y el remito de ayer termina diciendo otra cosa que la ficha de hoy.
 *
 * Esa decision sigue siendo la correcta para lo que se GUARDA. Lo que se
 * rompio fue lo que se MUESTRA: en Inventario, en el buscador del mostrador y en el
 * ticket, tres unidades distintas del mismo modelo aparecian con el mismo
 * texto —"IPHONE 15 PRO 256GB (USADO)"— y no habia forma de saber cual era
 * cual sin abrir cada ficha. Justo donde hay alguien esperando una respuesta.
 *
 * La solucion es componer el nombre al dibujarlo, no al guardarlo: la base es
 * el nombre tal cual esta en la base, y encima se le suma lo que la ficha sabe
 * y el nombre no dice.
 *
 * NO REPITE LO QUE YA ESTA ESCRITO
 * --------------------------------
 * Media lista es vieja: se cargo cuando el color y la bateria SI se escribian
 * adentro del nombre ("IPHONE 16 PRO 128GB DESERT (USADO 90%)"). Sobre esas
 * filas, agregar la columna daria "... DESERT (USADO 90%) · Desert Titanium ·
 * bat. 90%". Por eso cada parte se agrega solo si el nombre no la dice ya, y
 * ante la duda gana lo que esta escrito: son los datos que alguien tipeo
 * mirando el equipo.
 */
import { colorDeIphone } from "./colores";
import { etiquetaCapacidad } from "@/components/iphones/logica";

/**
 * Lo unico que el armador necesita. Es una forma suelta a proposito: la
 * cumplen `Producto`, la fila de `v_stock_valorizado`, la de `v_reposicion` y
 * el resultado del buscador, que son cuatro tipos distintos de cuatro fuentes
 * distintas y todos hablan del mismo telefono.
 */
export interface DatosDelNombre {
  nombre: string;
  color?: string | null;
  memoria_gb?: number | null;
  bateria_pct?: number | null;
}

/** Para comparar contra el nombre: sin acentos, sin signos, todo junto. */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
}

/** El nombre ya menciona esa palabra (o ese color escrito de otra manera). */
function yaLoDice(nombre: string, candidatos: ReadonlyArray<string | null | undefined>): boolean {
  const base = ` ${normalizar(nombre)} `;
  return candidatos.some((c) => {
    const n = c ? normalizar(c) : "";
    return n !== "" && base.includes(` ${n} `);
  });
}

/**
 * El nombre ya trae una capacidad.
 *
 * Se mira que HAYA una, no que coincida con la columna. Si el nombre dice
 * 128GB y la ficha 256, hay un dato mal cargado, y escribir los dos juntos no
 * lo arregla: lo esconde atras de una fila que se lee "128GB · 256 GB".
 */
function yaTieneCapacidad(nombre: string): boolean {
  return /\d+\s*(GB|TB)\b/i.test(nombre);
}

/** El nombre ya trae un porcentaje: los usados viejos lo tenian adentro. */
function yaTieneBateria(nombre: string): boolean {
  return /\d+\s*%/.test(nombre);
}

/**
 * El color, escrito para una fila de lista.
 *
 * Apple al rojo lo llama "(PRODUCT)RED" y asi esta en la tabla de colores, que
 * es lo correcto donde el nombre se lee entero: el tooltip del cuadradito, el
 * texto accesible, la publicacion. Adentro de un renglon de stock —al lado de
 * un nombre largo y una bateria— ese parentesis ocupa la mitad de lo que se ve
 * y no agrega nada: quien recorre la lista busca "el rojo".
 *
 * Solo se toca como se ESCRIBE acá. El color guardado no cambia, y la tabla de
 * colores sigue diciendo (PRODUCT)RED donde corresponde.
 */
function paraElNombre(etiqueta: string): string {
  const sinPrefijo = etiqueta.replace(/^\(PRODUCT\)\s*/i, "");
  if (sinPrefijo === etiqueta) return etiqueta;
  // Sin el prefijo queda "RED", en mayusculas, gritando al lado de "Black" y
  // "Purple". Se escribe como los otros.
  return sinPrefijo.charAt(0) + sinPrefijo.slice(1).toLowerCase();
}

/**
 * El nombre completo de la unidad: "IPHONE 15 PRO 256GB (USADO) · Black ·
 * bat. 89%".
 *
 * El color va con el nombre que le pone Apple y no con el que tipeo quien
 * cargo ("BLACK" → "Black Titanium" en un 15 Pro): es el mismo criterio del
 * cuadradito de color, que tampoco repite el texto crudo. Un color que la
 * tabla no conoce se escribe tal cual, sin inventar nada.
 */
export function nombreCompleto(p: DatosDelNombre): string {
  const base = p.nombre?.trim() ?? "";
  if (!base) return "";

  const partes: string[] = [base];

  const memoria = etiquetaCapacidad(p.memoria_gb ?? 0);
  if (memoria && !yaTieneCapacidad(base)) partes.push(memoria);

  const color = p.color?.trim();
  if (color) {
    const etiqueta = paraElNombre(colorDeIphone(color)?.etiqueta ?? color);
    if (!yaLoDice(base, [color, etiqueta])) partes.push(etiqueta);
  }

  if (p.bateria_pct !== null && p.bateria_pct !== undefined && !yaTieneBateria(base)) {
    partes.push(`bat. ${p.bateria_pct}%`);
  }

  return partes.join(" · ");
}
